import { useState } from "react";
import { Tooltip } from "@primer/react";
import { deterministicColorIndex, qualitativeColors } from "../../lib/deterministicColor.js";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../PortfolioValue/PortfolioValue.jsx";
import "./AllocationMap.css";

export function AllocationMap({ columns, onSelectionChange, totalBasisPoints }) {
  const [selectedPath, setSelectedPath] = useState([]);
  const { value } = usePortfolioValue();
  const activePath = validSelectedPath(columns, selectedPath);
  const rows = visibleRows(columns, activePath);

  if (!columns.length || totalBasisPoints <= 0) return null;

  function selectNode(node) {
    const nextPath = pathStartsWith(activePath, node.path) ? node.path.slice(0, -1) : node.path;
    setSelectedPath(nextPath);
    onSelectionChange?.(nextPath);
  }

  return (
    <section className="allocation-map" aria-label="Allocation map">
      {rows.map(({ column, nodes }, depth) => {
        const colors = uniqueColors(nodes);
        const selectedNode = nodes.find((node) => pathStartsWith(activePath, node.path));
        return (
          <div className="meter" role="group" aria-label={column.label} key={column.id}>
            {nodes.map((node, index) => {
              const parentPercentage = percentage(node.basisPoints, node.parentBasisPoints);
              const portfolioPercentage = percentage(node.basisPoints, 10000);
              const selected = selectedNode === node;
              const dimmed = Boolean(selectedNode) && !selected;
              const amount = value === null ? "" : ` · ${formatPortfolioAmount(amountForBasisPoints(value, node.basisPoints))}`;
              const context = node.parentName === "portfolio"
                ? `${portfolioPercentage}% of portfolio`
                : `${parentPercentage}% of ${node.parentName} · ${portfolioPercentage}% of portfolio`;
              const tooltip = `${node.label} · ${context}${amount}`;
              const content = <><span>{node.label}</span><small>{parentPercentage}%</small></>;
              const style = { "--segment-color": colors[index], width: `${node.basisPoints / node.parentBasisPoints * 100}%` };
              const className = `segment${selected ? " selected" : ""}${dimmed ? " dimmed" : ""}`;
              return (
                <Tooltip text={tooltip} direction={depth === rows.length - 1 ? "n" : "s"} key={node.path.join("/")}>
                  {node.leaf
                    ? <a className={className} href={node.href} aria-label={tooltip} style={style}>{content}</a>
                    : <button className={className} type="button" aria-expanded={selected} aria-label={tooltip} onClick={() => selectNode(node)} style={style}>{content}</button>}
                </Tooltip>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}

function visibleRows(columns, selectedPath) {
  const rows = [];
  for (const [depth, column] of columns.entries()) {
    if (depth > selectedPath.length) break;
    const parentPath = selectedPath.slice(0, depth);
    const nodes = column.nodes.filter((node) => node.path.slice(0, depth).every((part, index) => part === parentPath[index]));
    if (!nodes.length) break;
    rows.push({ column, nodes });
  }
  return rows;
}

function validSelectedPath(columns, selectedPath) {
  let validPath = [];
  for (const [depth, part] of selectedPath.entries()) {
    const candidate = [...validPath, part];
    if (!columns[depth]?.nodes.some((node) => pathsEqual(node.path, candidate))) break;
    validPath = candidate;
  }
  return validPath;
}

function pathStartsWith(path, prefix) {
  return path.length >= prefix.length && prefix.every((part, index) => path[index] === part);
}

function pathsEqual(left, right) {
  return left.length === right.length && left.every((part, index) => part === right[index]);
}

function uniqueColors(nodes) {
  const used = new Set();
  return nodes.map((node) => {
    let index = deterministicColorIndex(node.label, qualitativeColors.length);
    while (used.has(index) && used.size < qualitativeColors.length) index = (index + 1) % qualitativeColors.length;
    used.add(index);
    return qualitativeColors[index];
  });
}

function percentage(basisPoints, totalBasisPoints) {
  if (!totalBasisPoints) return "0";
  return (basisPoints / totalBasisPoints * 100).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}
